import { CommonModule } from '@angular/common';
import { Component, ElementRef, EventEmitter, inject, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { CardModule } from 'primeng/card';
import { FloatLabelModule } from 'primeng/floatlabel';
import { InputNumberModule } from 'primeng/inputnumber';
import { SelectModule } from 'primeng/select';
import { TooltipModule } from 'primeng/tooltip';
import { firstValueFrom } from 'rxjs';
import { afterRender } from '../../../../common/after-render';
import { notifyAndTrackError } from '../../../../common/error';
import { isNotEmpty, isStringEmpty, isStringEqual } from '../../../../common/functions';
import { warningMsg } from '../../../../common/messages';
import {} from '../../../../common/sorts';
import { ProcessingComponent } from '../../../../components';
import { LockInitialWidthDirective } from '../../../../directives/lock-initial-width.directive';
import { AppInitService } from '../../../../services/app-init.service';
import { AppInsightsService } from '../../../../services/app-insights.service';
import { IPermits, IUploadUrlResult, UploadService } from '../../../../services/insecta';
import { PermitsAdminService } from '../../../../services/insecta/admin/permitslist-admin.service';
@Component({
  selector: 'app-insect-item',
  standalone: true,
  imports: [
    SelectModule,
    CommonModule,
    FormsModule,
    InputNumberModule,
    FloatLabelModule,
    CardModule,
    LockInitialWidthDirective,
    ProcessingComponent,
    TooltipModule
  ],
  templateUrl: './insect-item.component.html'
})
export class InsectItemComponent implements OnChanges {
  private readonly permitService = inject(PermitsAdminService);
  private readonly msgService = inject(MessageService);
  private readonly appInsights = inject(AppInsightsService);
  private readonly uploadService = inject(UploadService);
  private readonly appInit = inject(AppInitService);
  @ViewChild('insectVariantSelect', { read: ElementRef }) insectVariantSelect!: ElementRef;
  @ViewChild('growthStageSelect', { read: ElementRef }) growthStageSelect!: ElementRef;
  @ViewChild('permitSelect', { read: ElementRef }) permitSelect!: ElementRef;

  @Input() catalogInformation!: any[];
  @Input() reset: boolean = false;
  @Input() editingOrderItemInsectVariant: any;
  @Input() editingInstance: boolean = false;
  @Input() editingMode!: string;
  @Input() editingOrderItemDetails: any;
  @Output() insectItemChangeEvent = new EventEmitter<any>();

  insectsVariantsConcatenated: any[] = [];
  insectGrowthStages: any[] = [];
  permits: IPermits[] = [];
  editInsectDetails = {
    label: '',
    insectId: null,
    leadTime: null,
    value: null,
    catalogInsectId: null,
    catalogInsectVariantId: null,
    insectGrowthStage: ''
  };
  insectItem = {
    quantity: null,
    insectVariantId: null,
    insectGrowthStageId: null,
    catalogInsectGrowthStageId: null,
    catalogInsectVariantId: null,
    permitId: null
  };
  growthStageDisabled: boolean = true;
  selectedPermit: any;
  isFetchingPdfUrl = false;

  private permitsPromise: Promise<void> | undefined;

  async ngOnChanges(changes: SimpleChanges): Promise<void> {
    if (!this.permitsPromise) {
      this.permitsPromise = this.getPermits();
    }
    await this.permitsPromise;

    if (this.reset) {
      this.insectItem = {
        quantity: null,
        insectVariantId: null,
        insectGrowthStageId: null,
        catalogInsectGrowthStageId: null,
        catalogInsectVariantId: null,
        permitId: null
      };
      afterRender(() => this.emitInsectItem());
    }

    this.growthStageDisabled = true;
    this.getInsectVariants();

    if (changes['editingOrderItemInsectVariant']) {
      if (!isStringEmpty(this.editingOrderItemInsectVariant.catalogInsectVariantId)) {
        this.insectItem.quantity = this.editingOrderItemInsectVariant.insectQuantity;
        this.insectItem.permitId = this.editingOrderItemInsectVariant.insectPermitId;
        this.selectedPermit = this.permits.find((x) => x.id === this.insectItem.permitId);
        if (this.selectedPermit && this.selectedPermit.uploadId) {
          await this.mapPdfUrl(this.selectedPermit.uploadId);
        }

        const variantSelected = this.catalogInformation
          .flatMap((info: any) => info.Variants)
          .find((variant: any) => isStringEqual(variant.CatalogInsectVariantId, this.editingOrderItemInsectVariant.catalogInsectVariantId));

        this.insectItem.insectVariantId = variantSelected.InsectVariantId;
        this.insectItem.catalogInsectVariantId = variantSelected.CatalogInsectVariantId;

        const containerSelected = this.catalogInformation
          .flatMap((info: any) => info.Containers)
          .find((container: any) =>
            isStringEqual(container.CatalogContainerInsectGrowthStageId, this.editingOrderItemInsectVariant.catalogContainerInsectGrowthStageId)
          );
        const catalogInsectGrowthStageIdSelected = containerSelected?.CatalogInsectGrowthStageId;
        const growthStageSelected = this.catalogInformation
          .flatMap((info: any) => info.GrowthStages)
          .find((gs: any) => isStringEqual(gs.CatalogInsectGrowthStageId, catalogInsectGrowthStageIdSelected));

        this.mapGrowthStages({ value: this.insectItem.insectVariantId });
        this.insectItem.insectGrowthStageId = growthStageSelected ? growthStageSelected.InsectGrowthStageId : null;
        this.insectItem.catalogInsectGrowthStageId = growthStageSelected ? growthStageSelected.CatalogInsectGrowthStageId : null;
        afterRender(() => this.emitInsectItem());
      }
    }
  }

  getInsectVariants() {
    const insectsVariantsConcatenated: any = [];
    const userSettings = this.appInit.appUser?.userSettingsJson ? JSON.parse(this.appInit.appUser.userSettingsJson) : {};
    const useCommonName = userSettings.useCommonName ?? false;
    const useSiteCode = userSettings.useSiteCode ?? false;

    this.catalogInformation.forEach((item: any) => {
      item.Variants.map((variant: any) => {
        if (!insectsVariantsConcatenated.some((insect: any) => insect.value === variant.CatalogInsectVariantId)) {
          const insectName = useCommonName && isNotEmpty(item.CommonName) ? `${item.ScientificName}: ${item.CommonName}` : `${item.ScientificName}`;
          const variantName =
            useSiteCode && isNotEmpty(variant.SiteCode) ? `(${variant.VariantTypeName}: ${variant.SiteCode})` : `(${variant.VariantTypeName})`;
          const searchText = [item.ScientificName, item.CommonName, variant.VariantTypeName, variant.SiteCode]
            .filter(Boolean)
            .join('||')
            .toLowerCase();

          insectsVariantsConcatenated.push({
            label: `${insectName} ${variantName}`,
            searchText: `||${searchText}||`,
            insectId: item.InsectId,
            leadTime: variant.LeadTime,
            value: variant.InsectVariantId,
            catalogInsectId: variant.CatalogInsectId,
            catalogInsectVariantId: variant.CatalogInsectVariantId
          });
        }
      });
    });
    this.insectsVariantsConcatenated = insectsVariantsConcatenated.sort((a: any, b: any) => a.label.localeCompare(b.label));
  }

  getSelectedVariantTooltip(): string {
    if (!this.insectItem.insectVariantId) return '';
    const selected = this.insectsVariantsConcatenated.find((v: any) => v.value === this.insectItem.insectVariantId);
    return selected ? selected.label : '';
  }

  mapGrowthStages(event: any) {
    this.insectItem.catalogInsectGrowthStageId = null;
    this.insectItem.insectGrowthStageId = null;

    const variantId = event.value;
    const insectVariant = this.insectsVariantsConcatenated.find((insect: any) => insect.value === variantId);
    const insect = this.catalogInformation.find((item: any) => item.CatalogInsectId === insectVariant.catalogInsectId);

    this.insectGrowthStages = insect.GrowthStages.filter((x: any) => x.CatalogInsectId === insectVariant.catalogInsectId);
    this.insectGrowthStages = this.insectGrowthStages.sort((a: any, b: any) =>
      a.GrowthStageSortOrder === b.GrowthStageSortOrder
        ? a.GrowthStageName.localeCompare(b.GrowthStageName)
        : a.GrowthStageSortOrder - b.GrowthStageSortOrder
    );
    this.growthStageDisabled = false;
    afterRender(() => this.emitInsectItem());
  }

  async getPermits(): Promise<void> {
    try {
      const result = await firstValueFrom(this.permitService.getPermitsAll());
      this.permits = result.filter((x) => x.isActive);
      this.permits = this.permits.map((x: any) => ({ ...x, pdfUrl: null }));
    } catch (err) {
      warningMsg(this.msgService, 'Error in fetching permits');
      notifyAndTrackError(this.msgService, this.appInsights, err, { component: 'InsectItemComponent', action: 'LoadPermits' });
    }
  }

  emitInsectItem(): any {
    if (this.insectItem.insectVariantId && this.insectsVariantsConcatenated && this.insectsVariantsConcatenated.length > 0) {
      const variant = this.insectsVariantsConcatenated.find((x) => x.value === this.insectItem.insectVariantId);
      this.insectItem.catalogInsectVariantId = variant ? variant.catalogInsectVariantId : null;
    }

    if (this.insectItem.insectGrowthStageId && this.insectGrowthStages && this.insectGrowthStages.length > 0) {
      const growthStage = this.insectGrowthStages.find((x) => x.InsectGrowthStageId === this.insectItem.insectGrowthStageId);
      this.insectItem.catalogInsectGrowthStageId = growthStage ? growthStage.CatalogInsectGrowthStageId : null;
    }

    this.insectItemChangeEvent.emit(this.insectItem);
  }

  async permitChange(event: any) {
    this.selectedPermit = this.permits.find((x) => x.id === event.value);
    if (this.selectedPermit && isNotEmpty(this.selectedPermit.uploadId)) {
      await this.mapPdfUrl(this.selectedPermit.uploadId);
    } else if (this.selectedPermit) {
      this.selectedPermit.pdfUrl = null;
    }
    this.emitInsectItem();
  }

  async mapPdfUrl(uploadId: string): Promise<void> {
    this.isFetchingPdfUrl = true;
    try {
      const result: IUploadUrlResult = await firstValueFrom(this.uploadService.getUploadUrl(uploadId));
      this.selectedPermit.pdfUrl = result.url;
    } catch (err) {
      notifyAndTrackError(this.msgService, this.appInsights, err, { component: 'InsectAdminComponent', action: 'LinkImage:Error' });
      notifyAndTrackError(this.msgService, this.appInsights, 'Failed to get image URL', {
        component: 'InsectAdminComponent',
        action: 'LinkImage:Message'
      });
    } finally {
      this.isFetchingPdfUrl = false;
    }
  }
}
